import * as React from 'react';

import user from 'js/lib/user';

import type { RecoveryEmailData } from 'bundles/account-settings/types/RecoveryEmail';
import { getUserRecoveryEmail } from 'bundles/account-settings/utils/RecoveryEmailAPIUtils';

type InjectedProps = {
  recoveryEmailData?: RecoveryEmailData | null;
  setRecoveryEmailData?: (data: RecoveryEmailData | null) => void;
  isRecoveryEmailLoading?: boolean;
  recoveryEmailError?: unknown;
};

export default function withRecoveryEmail<P extends object>(WrappedComponent: React.ComponentType<P & InjectedProps>) {
  const WithRecoveryEmail = (props: P & { orgId: string }) => {
    const [recoveryEmailData, setRecoveryEmailData] = React.useState<RecoveryEmailData | null>(null);
    const [isRecoveryEmailLoading, setIsRecoveryEmailLoading] = React.useState<boolean>(false);
    const [recoveryEmailError, setRecoveryEmailError] = React.useState<unknown | null>(null);

    React.useEffect(() => {
      let isMounted = true;
      const userId = user.get().id as number | undefined;

      if (!userId) {
        setRecoveryEmailData(null);
        setIsRecoveryEmailLoading(false);
        setRecoveryEmailError(null);
        return () => {
          isMounted = false;
        };
      }

      setIsRecoveryEmailLoading(true);
      getUserRecoveryEmail({ userId })
        .then((res) => {
          if (!isMounted) return;
          if ('recoveryEmail' in res) {
            setRecoveryEmailData(res.recoveryEmail as RecoveryEmailData);
          } else {
            setRecoveryEmailData(null);
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          setRecoveryEmailError(err);
        })
        .finally(() => {
          if (!isMounted) return;
          setIsRecoveryEmailLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }, [props.orgId]);

    return (
      <WrappedComponent
        {...props}
        recoveryEmailData={recoveryEmailData}
        setRecoveryEmailData={setRecoveryEmailData}
        isRecoveryEmailLoading={isRecoveryEmailLoading}
        recoveryEmailError={recoveryEmailError}
      />
    );
  };

  return WithRecoveryEmail;
}
